import React from 'react';
import {
	ClubVWTrackingEvent,
	ClubVWTrackingManager,
	TrackingEvent,
	TrackingEventInfo,
	TrackingLink,
} from './tracking-types';
import { useFeatureService } from '../context/portal-context';

const baseEventInfo: TrackingEventInfo = {
	brand: 'passenger',
	pageCategory: 'Club Volkswagen',
	sectionName: undefined,
	templateType: 'Feature App Template',
};

const createTrackingLink = (linkName: string | undefined): TrackingLink | undefined => {
	const link = {
		url: window ? window.location.href : undefined,
	};

	return { ...link, name: linkName };
};

export const useTrackingManager = (): ClubVWTrackingManager => {
	const { tracking: trackingManager } = useFeatureService();

	return React.useMemo((): ClubVWTrackingManager => {
		function track(action: string, event: ClubVWTrackingEvent): void {
			if (trackingManager) {
				trackingManager.track(action, event as TrackingEvent);
			}
		}

		return {
			trackClubVWFormLoad(formInfo, eventInfo?, ownershipInfo?) {
				track('VWBasic_Form_Load', {
					form: formInfo,
					eventInfo: { ...baseEventInfo, ...eventInfo },
					ownership: ownershipInfo,
				});
			},
			trackClubVWFormCTAClick(formInfo, eventInfo, ownershipInfo?) {
				track('VWBasic_FormCTA_Click', {
					form: formInfo,
					eventInfo: { ...baseEventInfo, ...eventInfo },
					ownership: ownershipInfo,
				});
			},
			trackClubVWFormButtonClick(formInfo, eventInfo, ownershipInfo?) {
				track('VWBasic_FormButton_Click', {
					form: formInfo,
					eventInfo: { ...baseEventInfo, ...eventInfo },
					ownership: ownershipInfo,
				});
			},
			trackClubVWFormSubmissionMessageLoad(formInfo, eventInfo, ownershipInfo?) {
				track('VWBasic_FormSubmissionMessage_Load', {
					form: formInfo,
					eventInfo: { ...baseEventInfo, ...eventInfo },
					ownership: ownershipInfo,
				});
			},
			trackClubVWFormSubmissionSuccessMessageLoad(formInfo, eventInfo, ownershipInfo?) {
				track('VWBasic_FormSubmissionSuccessMessage_Load', {
					form: formInfo,
					eventInfo: { ...baseEventInfo, ...eventInfo },
					ownership: ownershipInfo,
				});
			},
			trackClubVWLayerLoad(eventInfo?, ownershipInfo?, configurationInfo?, contentInfo?) {
				track('VWBasic_Layer_Load', {
					eventInfo: { ...baseEventInfo, ...eventInfo },
					ownership: ownershipInfo,
					configuration: configurationInfo,
					content: { Name: contentInfo },
					link: createTrackingLink(eventInfo?.contentId),
				});
			},
			trackClubVWButtonClick(linkName, eventInfo?, ownershipInfo?, configurationInfo?, contentInfo?) {
				track('VWBasic_Button_Click', {
					eventInfo: { ...baseEventInfo, ...eventInfo },
					link: createTrackingLink(linkName),
					ownership: ownershipInfo,
					configuration: configurationInfo,
					content: contentInfo ? { Name: contentInfo } : undefined,
				});
			},
			trackClubVWDownloadClick(linkName, eventInfo?, ownershipInfo?, configurationInfo?, contentInfo?) {
				track('VWBasic_Download_Click', {
					eventInfo: { ...baseEventInfo, ...eventInfo },
					link: createTrackingLink(linkName),
					ownership: ownershipInfo,
					configuration: configurationInfo,
					content: contentInfo ? { Name: contentInfo } : undefined,
				});
			},
			trackClubVWLayerCloseClick(linkName, eventInfo, configurationInfo, contentInfo) {
				track('VWBasic_LayerClose_Click', {
					eventInfo: { ...baseEventInfo, ...eventInfo },
					link: createTrackingLink(linkName),
					configuration: configurationInfo,
					content: contentInfo ? { Name: contentInfo } : undefined,
				});
			},
			trackClubVWLinkClick(linkName, eventInfo, ownershipInfo?, configurationInfo?, contentInfo?) {
				track('VWBasic_Link_Click', {
					eventInfo: { ...baseEventInfo, ...eventInfo },
					link: createTrackingLink(linkName),
					ownership: ownershipInfo,
					configuration: configurationInfo,
					content: contentInfo ? { Name: contentInfo } : undefined,
				});
			},
			trackClubVWGenericFeatureAppLoad(eventInfo, configurationInfo?, ownershipInfo?) {
				track('VWBasic_GenericFeatureApp_Load', {
					configuration: configurationInfo,
					eventInfo: { ...baseEventInfo, ...eventInfo },
					ownership: ownershipInfo,
				});
			},
			trackClubVWInfoMessageLoad(eventInfo, configurationInfo, contentInfo) {
				track('VWBasic_InfoMessage_Load', {
					configuration: configurationInfo,
					eventInfo: { ...baseEventInfo, ...eventInfo },
					link: createTrackingLink(eventInfo?.contentId),
					content: { Name: contentInfo },
				});
			},
			trackClubVWErrorMessageLoad(errorinfo, eventInfo, configurationInfo, contentInfo) {
				track('VWBasic_GenericErrorMessage_Load', {
					error: errorinfo,
					configuration: configurationInfo,
					eventInfo: { ...baseEventInfo, ...eventInfo },
					content: { Name: contentInfo },
				});
			},
			trackClubVWAccordionOpen(linkName, accordionInfo, ownershipInfo?, eventInfo?, contentInfo?, configurationInfo?) {
				track('VWBasic_AccordionOpen_Click', {
					link: createTrackingLink(linkName),
					accordion: accordionInfo,
					ownership: ownershipInfo,
					eventInfo: { ...baseEventInfo, ...eventInfo },
					content: { Name: contentInfo },
					configuration: configurationInfo,
				});
			},
			trackClubVWAccordionClose(linkName, accordionInfo, ownershipInfo?, eventInfo?, contentInfo?, configurationInfo?) {
				track('VWBasic_AccordionClose_Click', {
					link: createTrackingLink(linkName),
					accordion: accordionInfo,
					ownership: ownershipInfo,
					eventInfo: { ...baseEventInfo, ...eventInfo },
					content: { Name: contentInfo },
					configuration: configurationInfo,
				});
			},
		};
	}, [trackingManager]);
};
